import type { RefObject } from 'react';
import { useEffect, useState } from 'react';
import PlayerBar from '../PlayerBar';

interface StickyRadioBarProps {
  // Ref to the hero player element. The bar stays hidden while the
  // hero is in view and reveals once the user scrolls past its top
  // edge — so the user always sees one player surface, never two
  // stacked at the bottom plus the big hero at the top.
  heroRef: RefObject<HTMLElement | null>;
}

/**
 * Scroll-aware wrapper around the existing global PlayerBar.
 *
 * On the radio-first homepage we don't render PlayerBar from Layout
 * (see Layout.tsx — `!isOnRadioPage && <PlayerBar />`). Instead this
 * component watches the hero player's position and renders PlayerBar
 * only after the hero scrolls past, so the user gets the full bar
 * (track info, progress, volume) once they've moved away from the
 * primary controls — but not while the big hero is still on screen.
 */
export function StickyRadioBar({ heroRef }: StickyRadioBarProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;

    // Show the bar as soon as the TOP edge of the hero scrolls
    // above the viewport. Tracking the top (rather than waiting
    // for the bottom to clear) means the bar appears at the first
    // meaningful scroll — even on pages where the hero is taller
    // than the available scroll distance below it.
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      setVisible(rect.top < 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [heroRef]);

  if (!visible) return null;
  return <PlayerBar />;
}
